import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { usePassCodeur } from '../lib/pass-codeur-context';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Crown, Zap, BookOpen, Code, Share, Star, RefreshCw } from 'lucide-react';
import { UpdateIcon, TimeIcon, RefreshIcon, DiscordIcon } from '../components/icons/SvgIcons';
import { useToast } from '../lib/use-toast';
import { createCheckoutSession } from '../lib/stripe-service';
import { getAllCoursesStatic } from './cours/courses-data';
import { PASS_CODEUR_PRICING } from '../constants';
import SEO from '../components/SEO';

const PassCodeur = () => {
  const { currentUser } = useAuth();
  const { hasPassCodeur, isLoading: passCodeurLoading } = usePassCodeur();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Scroll vers le haut quand on arrive sur la page
  useEffect(() => {
    // Forcer le scroll immédiatement
    window.scrollTo(0, 0);
    
    // Puis un scroll smooth pour s'assurer
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, []);

  // Prix du pass depuis les constantes
  const passPrice = PASS_CODEUR_PRICING.CURRENT_PRICE;
  const originalPrice = PASS_CODEUR_PRICING.ORIGINAL_PRICE;

  // Récupérer tous les cours depuis les données statiques
  const allCourses = getAllCoursesStatic();

  // Icône Discord personnalisée
  const DiscordIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );

  // Avantages inclus dans le pass
  const passFeatures = [
    { icon: <BookOpen className="h-5 w-5" />, text: "Accès à tous les cours" },
    { icon: <Code className="h-5 w-5" />, text: "Un editeur pour créer tes jeux" },
    { icon: <Share className="h-5 w-5" />, text: "Un espace pour partager tes créations" },
    { icon: <RefreshIcon className="h-5 w-5" />, text: "Mises à jour gratuites à vie *" },
    { icon: <DiscordIcon className="h-5 w-5" />, text: "Support Discord" },
  ];

  const handlePurchase = async () => {
    if (!currentUser) {
      toast({
        title: "Connexion requise",
        description: "Veuillez vous connecter pour acheter le Pass Codeur.",
        variant: "default",
      });
      navigate('/login?redirect=/pass-codeur');
      return;
    }

    try {
      setIsLoading(true);
      
      // Créer une session de paiement Stripe
      const session = await createCheckoutSession({
        userId: currentUser.uid,
        amount: PASS_CODEUR_PRICING.CURRENT_PRICE_CENTS, // Utiliser la constante en centimes
        currency: PASS_CODEUR_PRICING.CURRENCY,
        userEmail: currentUser.email // Passer l'email du compte
      });

      // Rediriger vers Stripe Checkout
      window.location.href = session.url;
      
    } catch (error) {
      console.error("Erreur lors de la création de la session de paiement:", error);
      toast({
        title: "Erreur",
        description: "Impossible de traiter le paiement. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Note: Plus de redirection pour les utilisateurs non connectés
  // Ils peuvent voir la page complète mais devront se connecter pour acheter

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO 
        title="Pass Codeur - Accès à tous les cours - Mon Premier Jeu"
        description="Obtenez le Pass Codeur à prix réduit ! Accès illimité à tous les cours de programmation de jeux vidéo, éditeur intégré et support Discord."
        keywords="Pass Codeur, formation premium, cours programmation, jeux vidéo, réduction, offre limitée, p5.js"
        url="/pass-codeur"
        type="product"
      />
      <div className="max-w-5xl mx-auto">
        {/* En-tête */}
                 <div className="text-center mb-12">
           <div className="bg-slate-700 dark:bg-primary/20 backdrop-blur-sm rounded-full p-6 w-fit mx-auto mb-6">
             <Crown className="h-16 w-16 text-yellow-500" />
           </div>
          <h1 className="text-6xl md:text-7xl font-bold mb-4">
            <span className="bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300 bg-clip-text text-transparent">
              Pass Codeur
            </span>
          </h1>
          <p className="text-2xl ">
          Un seul paiement pour tout débloquer : cours, éditeur, espace de partage et support Discord
          </p>
        </div>

        {/* Message si l'utilisateur a déjà le Pass Codeur */}
        {hasPassCodeur && (
          <div className="mb-12 text-center">
            <Card className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm shadow-2xl border-2 border-green-200 dark:border-green-800 rounded-3xl">
              <CardHeader>
                <CardTitle className="text-3xl text-green-700 dark:text-green-400 flex items-center justify-center gap-3">
                  <Check className="h-8 w-8 text-green-600" />
                  Tu as déjà le Pass Codeur !
                </CardTitle>
                <CardDescription className="text-xl text-foreground">
                  Félicitations ! Tu as accès à tous les cours et fonctionnalités premium.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Carte principale - Masquée si l'utilisateur a déjà le Pass Codeur */}
        {!hasPassCodeur && (
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Détails du pass */}
          <Card className="relative overflow-hidden bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm shadow-2xl border-2 border-blue-200 dark:border-blue-800 rounded-3xl">
            <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-3 py-1 text-sm font-medium">
              Offre limitée
            </div>
            <CardHeader className="text-center pt-8">
              <CardTitle className="text-2xl">Pass Codeur</CardTitle>
              <CardDescription>
                Accès illimité à tous les cours
              </CardDescription>
              <div className="text-4xl font-bold text-green-600 mt-4">
                {passPrice.toFixed(2)} €
              </div>
              <div className="text-lg mb-1 flex items-center justify-center">
                <span className="text-red-500 line-through">
                  {originalPrice.toFixed(2)} €
                </span>
                <span className="ml-2 text-green-600 font-bold">
                  -{PASS_CODEUR_PRICING.DISCOUNT_PERCENTAGE}%
                </span>
              </div>
                             <div className="text-sm text-muted-foreground">
                 Paiement unique, accès à vie *
               </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {passFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="text-green-600">
                      {feature.icon}
                    </div>
                                         <span className="text-lg">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
                             <Button 
                 className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-xl py-8 text-white"
                 onClick={handlePurchase}
                 disabled={isLoading}
               >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Traitement...
                  </div>
                ) : (
                  <>Acheter le Pass Codeur</>
                )}
              </Button>
            </CardFooter>
          </Card>

          {/* Liste des cours inclus */}
          <Card className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm shadow-2xl border-2 border-blue-200 dark:border-blue-800 rounded-3xl">
            <CardHeader>
              <CardTitle>Cours accessibles</CardTitle>
              <CardDescription>
                Tous les cours sont débloqués avec votre pass
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {allCourses.map((course) => (
                  <div key={course.id} className="flex items-center gap-3 p-3 bg-muted/60 dark:bg-muted/30 rounded-lg">
                    <Check className="h-5 w-5 text-green-600" />
                                         <div>
                       <div className="font-semibold text-lg">{course.title}</div>
                       <div className="text-base text-muted-foreground">{course.description}</div>
                     </div>
                  </div>
                ))}
                <div className="text-center text-base text-muted-foreground mt-4">
                  + Tous les futurs cours sans frais supplémentaires
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Section des garanties avec style moderne - Masquée si l'utilisateur a déjà le Pass Codeur */}
        {!hasPassCodeur && (
        <div className="mt-16 mb-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">Pourquoi choisir le Pass Codeur ?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 p-8 rounded-2xl border border-emerald-200 dark:border-emerald-800 hover:shadow-lg transition-all">
              <div className="text-emerald-600 dark:text-emerald-400 mb-4 flex justify-center">
                <Zap className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center text-foreground">Accès immédiat</h3>
              <p className="text-muted-foreground text-center">
                Débloque tous les cours instantanément après l'achat.
              </p>
            </div>
                         <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 p-8 rounded-2xl border border-cyan-200 dark:border-cyan-800 hover:shadow-lg transition-all">
               <div className="text-cyan-600 dark:text-cyan-400 mb-4 flex justify-center">
                                   <TimeIcon className="h-12 w-12" />
               </div>
              <h3 className="text-xl font-bold mb-4 text-center text-foreground">
                Accès à vie *
              </h3>
              <p className="text-muted-foreground text-center">
                Un seul paiement pour un accès permanent. Aucun abonnement, aucune surprise !
              </p>
            </div>
                         <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 p-8 rounded-2xl border border-purple-200 dark:border-purple-800 hover:shadow-lg transition-all">
                               <div className="text-purple-600 dark:text-purple-400 mb-4 flex justify-center">
                  <RefreshIcon className="h-12 w-12" />
                </div>
              <h3 className="text-xl font-bold mb-4 text-center text-foreground">Mises à jour incluses</h3>
              <p className="text-muted-foreground text-center">
                Tous les nouveaux cours ajoutés gratuitement.
              </p>
                         </div>
           </div>

           
           {/* Note explicative */}
           <div className="mt-8 text-center">
             <p className="text-sm text-muted-foreground">
               * Accès conditionné au maintien de l'exploitation du service
             </p>
           </div>
         </div>
        )}

       
        </div>
    </div>
  );
};

export default PassCodeur; 